import React, { useEffect, useState } from 'react';
import MiniMap from './MiniMap';
import { toPublicUrl, withCacheBuster } from './imageUtils';

// Renders a <geojson src="output/x.geojson"> reference map: fetches the file
// from the conversation's GCS output/ prefix and hands it to MiniMap. This is
// how tool-produced geometry (e.g. userdata get_asset_geometry, often far too
// large to travel inline through the model) reaches the map.
//
// Modeled on GraphChart (URL plumbing, load states, "unavailable" fallback —
// a map failure must never break the surrounding chat) and KmlPreviewChip
// (fetch-geometry-by-uri → MiniMap).

// Refuse absurd payloads before they reach JSON.parse / Mapbox. The upload path
// caps stored asset geometry at 0.4 MB, so 10 MB is generous headroom for any
// legitimate artifact.
const MAX_GEOJSON_BYTES = 10 * 1024 * 1024;

// Small per-session fetch cache, same shape as graphUtils' csvCache: dedupes
// re-renders, evicts FIFO, and drops rejected entries so a re-mount retries.
const geojsonCache = new Map<string, Promise<GeoJSON.FeatureCollection>>();
const GEOJSON_CACHE_MAX = 20;

// Accept what maps legitimately produce: a FeatureCollection, a bare Feature,
// or a bare geometry — normalized to a FeatureCollection for MiniMap. (Asset
// geometry passes through the server verbatim, so all three occur in the wild;
// same tolerance as AssetMapView's createAssetGeoJSONFeature.)
const GEOMETRY_TYPES = new Set([
  'Point', 'MultiPoint', 'LineString', 'MultiLineString',
  'Polygon', 'MultiPolygon', 'GeometryCollection',
]);

function normalizeToFeatureCollection(parsed: unknown): GeoJSON.FeatureCollection | null {
  if (!parsed || typeof parsed !== 'object') return null;
  const obj = parsed as { type?: string; features?: unknown; geometry?: unknown };

  if (obj.type === 'FeatureCollection' && Array.isArray(obj.features)) {
    return obj as GeoJSON.FeatureCollection;
  }
  if (obj.type === 'Feature' && obj.geometry) {
    return { type: 'FeatureCollection', features: [obj as GeoJSON.Feature] };
  }
  if (obj.type && GEOMETRY_TYPES.has(obj.type)) {
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry: obj as GeoJSON.Geometry, properties: {} }],
    };
  }
  return null;
}

async function fetchGeojson(url: string): Promise<GeoJSON.FeatureCollection> {
  const cached = geojsonCache.get(url);
  if (cached) return cached;

  const promise = (async () => {
    const res = await fetch(withCacheBuster(url));
    if (!res.ok) throw new Error(`geojson fetch failed: ${res.status}`);
    const len = Number(res.headers.get('content-length') || 0);
    if (len > MAX_GEOJSON_BYTES) throw new Error(`geojson too large: ${len} bytes`);
    const text = await res.text();
    if (text.length > MAX_GEOJSON_BYTES) throw new Error(`geojson too large: ${text.length} chars`);
    const normalized = normalizeToFeatureCollection(JSON.parse(text));
    if (!normalized) throw new Error('not a FeatureCollection, Feature, or geometry');
    return normalized;
  })();

  geojsonCache.set(url, promise);
  if (geojsonCache.size > GEOJSON_CACHE_MAX) {
    const oldest = geojsonCache.keys().next().value;
    if (oldest !== undefined) geojsonCache.delete(oldest);
  }
  promise.catch(() => geojsonCache.delete(url));
  return promise;
}

interface GeoJsonMapLoaderProps {
  // Opaque relative path from the <geojson src> tag, e.g. output/asset_geometry_x.geojson.
  // In shared (read-only) views the snapshot's s3img: marker has already been
  // presigned server-side, so this arrives as an absolute S3 URL instead —
  // toPublicUrl passes allowlisted hosts through unchanged.
  src: string;
  title?: string;
  conversationId?: string;
  fixedHeight?: number;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; geojson: GeoJSON.FeatureCollection }
  | { status: 'error' };

const GeoJsonMapLoader: React.FC<GeoJsonMapLoaderProps> = ({ src, title, conversationId, fixedHeight = 420 }) => {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    const url = toPublicUrl(src, conversationId);
    if (!url) {
      // toPublicUrl rejected the path (disallowed host/shape) — treat as error.
      setState({ status: 'error' });
      return;
    }

    fetchGeojson(url)
      .then((geojson) => { if (!cancelled) setState({ status: 'ready', geojson }); })
      .catch((err) => {
        console.warn('[GeoJsonMapLoader] failed to load geometry', src, err);
        if (!cancelled) setState({ status: 'error' });
      });

    return () => { cancelled = true; };
  }, [src, conversationId]);

  if (state.status === 'loading') {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50"
        style={{ height: fixedHeight }}
      >
        <div className="text-xs text-gray-500">Loading map…</div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50"
        style={{ height: 120 }}
      >
        <div className="text-xs text-gray-500 text-center">Map unavailable</div>
      </div>
    );
  }

  return <MiniMap geojson={state.geojson} title={title} fixedHeight={fixedHeight} />;
};

export default GeoJsonMapLoader;
